import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Check, X, Send, Loader2, User, Phone, MapPin, Mail, Clock,
  MessageSquare, CircleCheckBig, UserRound,
} from "lucide-react";
import { REF_STATUS_HEX, PRIORITY_HEX, pretty, hexA, fmtDate } from "./referralConstants";
import ExternalRefDocuments from "@/components/external/ExternalRefDocuments";

const SETTABLE = [
  { value: "in_progress", label: "In Progress" },
  { value: "appointment_completed", label: "Appointment Completed" },
  { value: "tour_completed", label: "Tour Completed" },
  { value: "quote_uploaded", label: "Quote / Estimate Ready" },
  { value: "follow_up_needed", label: "Follow-Up Needed" },
  { value: "completed", label: "Completed" },
];

const StatusChip = ({ status }) => {
  const hex = REF_STATUS_HEX[status] || REF_STATUS_HEX.draft;
  return <span data-testid="external-referral-detail-status" className="text-[12px] font-bold px-2.5 py-1 rounded-full"
    style={{ backgroundColor: hexA(hex, 0.14), color: hex }}>{pretty(status)}</span>;
};

export default function ExternalReferralDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/external/referrals/${id}`); setR(data); }
    catch (e) { toast.error(errMsg(e, "This referral is not available.")); nav("/external/referrals"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const { data } = await fn(); setR(data); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const accept = () => act(() => api.post(`/external/referrals/${id}/accept`).then((x) => x), "Referral accepted.");
  const decline = async () => {
    await act(() => api.post(`/external/referrals/${id}/decline`, { reason }).then((x) => x), "Referral declined.");
    setDeclineOpen(false); setReason("");
  };
  const updateStatus = async () => {
    if (!newStatus) return;
    await act(() => api.post(`/external/referrals/${id}/status`, { status: newStatus, note: statusNote || undefined }).then((x) => x), "Status updated.");
    setNewStatus(""); setStatusNote("");
  };
  const confirmOutcome = async () => {
    if (!outcome.trim()) { toast.error("Please describe the outcome."); return; }
    await act(() => api.post(`/external/referrals/${id}/outcome`, { outcome }).then((x) => x), "Outcome confirmed. Thank you!");
    setOutcomeOpen(false); setOutcome("");
  };
  const sendMessage = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/external/referrals/${id}/note`, { body: msg }); setMsg(""); await load(); toast.success("Message sent."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!r) return <div className="space-y-3"><Skeleton className="h-8 w-40" /><Skeleton className="h-32" /><Skeleton className="h-40" /></div>;

  const actions = r.actions || [];
  const canAccept = actions.includes("accept");
  const canUpdate = actions.includes("update_status");
  const canOutcome = actions.includes("confirm_outcome");
  const contact = r.staff_contact || r.relationship_manager;

  return (
    <div data-testid="external-referral-detail" className="space-y-5 max-w-3xl">
      <button onClick={() => nav("/external/referrals")} className="text-[13px] text-[#4B5563] hover:text-[#1C4358] flex items-center gap-1" data-testid="external-referral-back">
        <ArrowLeft size={15} /> Back to referrals
      </button>

      <Card className="p-5 sm:p-6 border-[#E6EDF2]">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusChip status={r.status} />
          <span className="text-[14px] font-semibold text-[#1C4358]">{pretty(r.referral_type)}</span>
          {r.priority && r.priority !== "normal" && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexA(PRIORITY_HEX[r.priority], 0.14), color: PRIORITY_HEX[r.priority] }}>{pretty(r.priority)} priority</span>
          )}
        </div>
        {r.summary && <p className="text-[15px] text-[#252221] mt-3">{r.summary}</p>}
        {r.next_step && (
          <div className="mt-3 rounded-lg bg-[#9C1E22]/8 border border-[#9C1E22]/20 px-3 py-2 text-[13px] text-[#9C1E22] font-medium" data-testid="external-referral-next-step">
            Next step: {r.next_step}
          </div>
        )}
        {(r.appointment_date || r.tour_date || r.estimate_date) && (
          <div className="mt-3 flex flex-wrap gap-4 text-[13px] text-[#4B5563]">
            {r.appointment_date && <span className="flex items-center gap-1"><Clock size={13} /> Appointment: {fmtDate(r.appointment_date)}</span>}
            {r.tour_date && <span className="flex items-center gap-1"><Clock size={13} /> Tour: {fmtDate(r.tour_date)}</span>}
            {r.estimate_date && <span className="flex items-center gap-1"><Clock size={13} /> Estimate: {fmtDate(r.estimate_date)}</span>}
          </div>
        )}
        {r.outcome && <div className="mt-3 text-[13px] text-[#2F7D5A] font-medium flex items-center gap-1"><CircleCheckBig size={14} /> Outcome: {r.outcome}</div>}
      </Card>

      {r.client && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-client">
          <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold">Client contact <span className="normal-case font-normal text-[#9AA5B1]">· shared for this referral only</span></div>
          <div className="mt-2 space-y-1 text-[14px] text-[#252221]">
            {r.client.name && <div className="flex items-center gap-2 font-semibold"><User size={14} className="text-[#1C4358]" /> {r.client.name}</div>}
            {r.client.phone && <a href={`tel:${r.client.phone}`} className="flex items-center gap-2 text-[#4B5563] hover:text-[#1C4358]"><Phone size={14} /> {r.client.phone}</a>}
            {r.client.email && <a href={`mailto:${r.client.email}`} className="flex items-center gap-2 text-[#4B5563] hover:text-[#1C4358]"><Mail size={14} /> {r.client.email}</a>}
            {r.client.address && <div className="flex items-center gap-2 text-[#4B5563]"><MapPin size={14} /> {r.client.address}</div>}
            {r.client.notes && <div className="text-[13px] text-[#4B5563] mt-1">{r.client.notes}</div>}
          </div>
        </Card>
      )}

      {/* Actions */}
      {(canAccept || canUpdate || canOutcome) && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-actions">
          <div className="text-[13px] font-semibold text-[#252221] mb-3">What would you like to do?</div>
          {canAccept && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={accept} disabled={busy} className="bg-[#2F7D5A] hover:bg-[#276849] text-white gap-1.5 rounded-full h-11 px-5" data-testid="external-referral-accept-btn">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Accept referral
              </Button>
              <Button onClick={() => setDeclineOpen(true)} disabled={busy} variant="outline" className="border-[#9C1E22]/40 text-[#9C1E22] gap-1.5 rounded-full h-11 px-5" data-testid="external-referral-decline-btn">
                <X size={16} /> Decline
              </Button>
            </div>
          )}
          {canUpdate && (
            <div className="space-y-2 mt-1">
              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <label className="text-[12px] text-[#4B5563]">Update status</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="h-11 mt-1" data-testid="external-referral-status-select"><SelectValue placeholder="Choose an update…" /></SelectTrigger>
                    <SelectContent>{SETTABLE.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={updateStatus} disabled={busy || !newStatus} className="bg-[#1C4358] hover:bg-[#153341] text-white gap-1.5 rounded-full h-11 px-5" data-testid="external-referral-status-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Update
                </Button>
              </div>
              <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Add an optional note for the MovingMyMama team…" rows={2} className="text-[14px]" data-testid="external-referral-status-note" />
              {canOutcome && (
                <Button onClick={() => setOutcomeOpen(true)} variant="outline" className="border-[#2F7D5A]/40 text-[#2F7D5A] gap-1.5 rounded-full h-10" data-testid="external-referral-outcome-btn">
                  <CircleCheckBig size={15} /> Confirm final outcome
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Documents */}
      <ExternalRefDocuments referralId={id} />

      {/* Timeline */}
      <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-timeline">
        <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><Clock size={15} /> Status timeline</div>
        <ol className="relative border-l border-[#E6EDF2] ml-1.5 space-y-4">
          {(r.timeline || []).slice().reverse().map((e, i) => (
            <li key={i} className="ml-4" data-testid="external-timeline-event">
              <span className="absolute -left-[7px] h-3 w-3 rounded-full" style={{ backgroundColor: REF_STATUS_HEX[e.status] || "#9AA5B1" }} />
              <div className="text-[13px] font-medium text-[#252221]">{e.label}</div>
              <div className="text-[11px] text-[#9AA5B1]">{e.by} · {fmtDate(e.at)}</div>
              {e.note && <div className="text-[12px] text-[#4B5563] mt-0.5">{e.note}</div>}
            </li>
          ))}
        </ol>
      </Card>

      {/* Messages */}
      <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-messages">
        <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><MessageSquare size={15} /> Messages with MovingMyMama</div>
        <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
          {(r.external_notes || []).length === 0 && <div className="text-[13px] text-muted-foreground">No messages yet.</div>}
          {(r.external_notes || []).map((n) => {
            const mine = n.author === "You";
            return (
              <div key={n.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[13px] ${mine ? "bg-[#1C4358] text-white" : "bg-[#F1F5F8] text-[#252221]"}`}>
                  <div className="text-[11px] opacity-70 mb-0.5">{n.author} · {fmtDate(n.at)}</div>
                  {n.body}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Send a message to your MovingMyMama contact…" rows={2} className="text-[14px]" data-testid="external-referral-message-input" />
          <Button onClick={sendMessage} disabled={busy || !msg.trim()} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white h-11 rounded-full px-4 gap-1.5" data-testid="external-referral-message-send">
            <Send size={15} /> Send
          </Button>
        </div>
      </Card>

      {contact && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-contact">
          <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><UserRound size={13} /> Your MovingMyMama contact</div>
          <div className="font-semibold text-[#252221] mt-1.5">{contact.full_name}</div>
          <div className="flex flex-wrap gap-4 mt-1.5 text-[14px] text-[#4B5563]">
            {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-[#1C4358]"><Mail size={14} /> {contact.email}</a>}
            {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-[#1C4358]"><Phone size={14} /> {contact.phone}</a>}
          </div>
        </Card>
      )}

      {/* Decline dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="external-decline-dialog">
          <DialogHeader>
            <DialogTitle>Decline this referral?</DialogTitle>
            <DialogDescription>Let the MovingMyMama team know why (optional). They'll be notified right away.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" rows={3} className="text-[14px]" data-testid="external-decline-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button onClick={decline} disabled={busy} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5" data-testid="external-decline-confirm">
              {busy && <Loader2 size={15} className="animate-spin" />} Decline referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome dialog */}
      <Dialog open={outcomeOpen} onOpenChange={setOutcomeOpen}>
        <DialogContent data-testid="external-outcome-dialog">
          <DialogHeader>
            <DialogTitle>Confirm final outcome</DialogTitle>
            <DialogDescription>Summarize how this referral concluded. This marks it complete.</DialogDescription>
          </DialogHeader>
          <Textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="e.g. Installed grab bars and ramp; family very happy." rows={3} className="text-[14px]" data-testid="external-outcome-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutcomeOpen(false)}>Cancel</Button>
            <Button onClick={confirmOutcome} disabled={busy} className="bg-[#2F7D5A] hover:bg-[#276849] text-white gap-1.5" data-testid="external-outcome-confirm">
              {busy && <Loader2 size={15} className="animate-spin" />} Confirm complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
